import React, { useCallback } from 'react';
import { useFeatureFlagContext } from '@eu-data-act/react-euda-commons/dist/index';

import {
  IconDashboardApplication,
  IconExternalLink,
  IconGlobe,
  IconLogPage,
  IconPrinter,
  IconShoppingCart,
  IconSupport,
  useTheme
} from '@veneer/core';

import { URLS } from '../../utils/urls';
import {
  ContentContainer,
  Divider,
  DoNotSeeDeviceContainer,
  HeaderContainer,
  RowButton,
  RowContent,
  StyledCard
} from '../../components/do-not-see-device/styled';

import * as T from '../../types/devices';

import { useDependencyManagerContext } from '../../contexts/dependencyManager';
import { useTabIndex } from '../../contexts/tab-index';

const DoNotSeeDevice: React.FC<T.DoNotSeeDevicePropsType> = () => {
  const theme = useTheme();
  const { getNextTabIndex } = useTabIndex();
  const {
    translate,
    northboundAPIs: {
      v1: {
        localization: { country, language }
      }
    }
  } = useDependencyManagerContext();
  const releasePlatformHpAnalytics = useFeatureFlagContext();

  const rows = [
    {
      content: translate('components.do-not-see-device.devices-purchased'),
      icon: <IconShoppingCart filled={false} />,
      link: URLS.devicesPurchased
    },
    {
      content: translate('components.do-not-see-device.get-support'),
      icon: <IconSupport filled={false} />,
      link: URLS.deviceSupportNotShown
    },
    {
      content: translate(
        'components.do-not-see-device.access-smart-admin-portal'
      ),
      icon: <IconDashboardApplication filled={false} />,
      link: URLS.accessSmartAdminPortal
    },
    {
      content: translate('components.do-not-see-device.access-print-os'),
      icon: <IconGlobe filled={false} />,
      link: URLS.accessPrintOS
    },
    {
      content: translate('components.do-not-see-device.view-or-add-printer'),
      icon: <IconPrinter filled={false} />,
      link: URLS.viewOrAddPrinter
    },
    {
      content: translate('components.do-not-see-device.request-personal-data'),
      icon: <IconLogPage filled={false} />,
      link: URLS.requestPersonalData(country, language)
    }
  ];

  const doNotSeeDeviceAnalyticsAttributes = useCallback(
    (id: string, value: string) => {
      return releasePlatformHpAnalytics
        ? {
            'data-gtm-category': 'linkClick',
            'data-gtm-id': `link-${id}`,
            'data-gtm-value': value
          }
        : {};
    },
    [releasePlatformHpAnalytics]
  );

  return (
    <DoNotSeeDeviceContainer
      $veneerTheme={theme}
      data-testid="react-device-management-mfe-do-not-see-device-container"
    >
      <StyledCard
        background="filled"
        border="outlined"
        $veneerTheme={theme}
      >
        <ContentContainer $veneerTheme={theme}>
          <HeaderContainer
            $veneerTheme={theme}
            data-testid="react-device-management-mfe-do-not-see-device-header-container"
          >
            <h3
              className="subtitle-regular"
              style={{ fontWeight: theme.typography.weight4 }}
            >
              {translate('components.do-not-see-device.title')}
            </h3>
          </HeaderContainer>
          {rows.map((row, index) => (
            <React.Fragment key={row.link}>
              <div data-testid="react-device-management-mfe-do-not-see-device-row-container">
                <RowButton
                  $veneerTheme={theme}
                  onClick={() => {
                    window.open(row.link, '_blank');
                  }}
                  aria-label={`${row.content} ${translate('components.do-not-see-device.new-tab')}`}
                  type="button"
                  tabIndex={getNextTabIndex()}
                  {...doNotSeeDeviceAnalyticsAttributes(row.link, row.content)}
                >
                  <RowContent $veneerTheme={theme}>
                    {row.icon}
                    <p className="label-regular">{row.content}</p>
                  </RowContent>
                  <IconExternalLink color={theme.color.foreground.light} />
                </RowButton>
              </div>
              {index < rows.length - 1 && <Divider $veneerTheme={theme} />}
            </React.Fragment>
          ))}
        </ContentContainer>
      </StyledCard>
    </DoNotSeeDeviceContainer>
  );
};

export default DoNotSeeDevice;
